import { useSubscription } from '@apollo/client';
import { DeviceEvent, DeviceEventActions } from '@oolio-group/domain';
import { storageSvc } from '@/services/storageServiceClient';
import { useSelection } from '@/libs/onboarding/hooks/useSelection';
import { DEVICE_EVENTS } from '@/queries/devices';

interface DeviceEventData {
  deviceEvents: DeviceEvent[];
}

interface EventHandler {
  action: DeviceEventActions;
  handler: (event: DeviceEvent) => Promise<void>;
}

/**
 * Subscribes to device events and handles logout like actions from POS backoffice.
 * @returns Subscription state and error information for device events.
 */
export const useDeviceEvents = (listeners: EventHandler[]) => {
  const { store, device } = useSelection(storageSvc);

  const { error, loading } = useSubscription<DeviceEventData>(DEVICE_EVENTS, {
    variables: {
      storeId: store?.id,
      deviceId: device?.id,
    },
    skip: !(store?.id && device?.id), // Skip if not enabled or missing required variables
    onData: ({ data: subscriptionData }) => {
      const events = subscriptionData.data?.deviceEvents ?? [];
      for (const event of events) {
        const matchingHandlers = listeners.filter(
          h => h.action === event.action,
        );
        for (const listener of matchingHandlers) {
          listener.handler(event).catch(error => {
            console.error('Error handling device event:', error);
          });
        }
      }
    },
  });

  return {
    isSubscriptionActive:
      Boolean(store?.id && device?.id) && !loading && !error,
    subscriptionError: error,
    subscriptionLoading: loading,
  };
};
